/**
 * InvoiceForm Component
 *
 * Modal for creating an invoice. Supports manual entry
 * or generation from a quotation.
 *
 * @module components/invoices/InvoiceForm
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  Table,
  Spinner,
  InputGroup,
  Alert,
} from 'react-bootstrap';
import { supabase } from '../../services/supabase';
import { QUOTATION_STATUSES_BILLABLE } from '../../services/invoiceService';
import { DEFAULT_TAX_RATE, INVOICEABLE_PROJECT_STATUSES } from '../../utils/constants';

/**
 * Tipos de línea aceptados por invoice_items.item_type (CHECK constraint).
 * #471: antes esto vivía hardcodeado en 'service' sin selector, así que toda
 * factura manual salía etiquetada como servicio sin que nadie lo eligiera.
 */
/**
 * Etiquetas en español de los estados de cotización.
 * El selector imprimía `({q.status})` crudo, o sea el enum de la base tal cual:
 * el usuario veía "COT-0012 — $4500.00 (approved)", un enum en inglés pegado a
 * una etiqueta en español. El enum es asunto de la base, no del usuario.
 */
const QUOTATION_STATUS_LABELS = {
  draft: 'Borrador',
  under_review: 'En revisión',
  approved: 'Aprobada',
  sent: 'Enviada',
  accepted: 'Aceptada',
  rejected: 'Rechazada',
};

const ITEM_TYPE_LABELS = {
  material: 'Material',
  labor: 'Mano de obra',
  service: 'Servicio',
  other: 'Otro',
};

/**
 * Fetch the projects the current user can invoice.
 *
 * The status list lives in `INVOICEABLE_PROJECT_STATUSES` (utils/constants) and
 * is the full DB CHECK set. It used to be a local `['active', 'quoted', 'draft']`
 * literal: `active` and `quoted` were removed from the CHECK by #296 and prod has
 * zero rows in `draft`, so this query returned 0 of 73 projects and creating an
 * invoice was impossible from anywhere in the app. Do not re-inline a status list
 * here — that drift is exactly what broke it.
 */
export const fetchProjects = async () => {
  const { data, error } = await supabase
    .from('projects')
    .select('id, project_number, title, client_id')
    .in('status', INVOICEABLE_PROJECT_STATUSES)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

/**
 * Fetch the quotations of a project that can legitimately be invoiced.
 *
 * La lista se IMPORTA de `services/invoiceService` — no se repite acá. Este
 * desplegable tenía su propia copia, `['approved', 'accepted']`, más angosta
 * que el gate que #629 puso en el servicio (`approved`, `sent`, `accepted`):
 * una cotización que avanzaba de `approved` a `sent` — o sea que YA salió hacia
 * el cliente, el caso normal de facturación — desaparecía del selector y no se
 * podía facturar desde la GUI, aunque `generateFromQuotation` la aceptaba y el
 * carril MCP la facturaba sin chistar. Avanzar en el workflow te quitaba una
 * capacidad, y el síntoma no se leía como un filtro mal puesto: se leía como
 * "esa cotización no existe".
 *
 * Misma enfermedad que `fetchProjects` acá arriba (#296) y que #353: dos copias
 * de la misma regla en dos archivos derivan en cuanto alguien toca una sola. No
 * re-inlinees la lista.
 */
export const fetchQuotationsForProject = async (projectId) => {
  const { data, error } = await supabase
    .from('quotations')
    .select('id, quotation_number, total, status')
    .eq('project_id', projectId)
    .in('status', QUOTATION_STATUSES_BILLABLE)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
};

/**
 * Fetch the client record for a given client_id.
 */
const fetchClient = async (clientId) => {
  if (!clientId) return null;
  const { data, error } = await supabase
    .from('clients')
    .select('id, company_name')
    .eq('id', clientId)
    .single();
  if (error) return null;
  return data;
};

const InvoiceForm = ({
  show,
  onHide,
  onSave,
  projectId,  // pre-selected project (when opened from a project page)
  quotations = [], // legacy prop (ignored; we now fetch them)
  loading = false,
}) => {
  const [source, setSource] = useState('manual');

  // Project / client state
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  // "No pude cargar" y "no hay ninguno" no son el mismo estado. Antes el
  // `.catch()` vaciaba la lista y el usuario veía un desplegable vacío, que se
  // lee como "no hay proyectos" — la misma mentira del estado vacío que se
  // arregla en las listas principales.
  const [projectsError, setProjectsError] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || '');
  const [selectedClient, setSelectedClient] = useState(null);

  // Quotation state (for "from quotation" mode)
  const [availableQuotations, setAvailableQuotations] = useState([]);
  const [quotationsLoading, setQuotationsLoading] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState('');

  // #457: the literal 13 lived here (and in the reset below) instead of the
  // DEFAULT_TAX_RATE constant. This is a genuine default for a blank manual
  // invoice — the user can still type 0 and it is preserved, since nothing
  // coalesces it on the way out — but the rate belongs in one place.
  const [formData, setFormData] = useState({
    invoice_date: new Date().toISOString().split('T')[0],
    payment_terms: 30,
    tax_rate: DEFAULT_TAX_RATE,
    notes: '',
  });
  const [items, setItems] = useState([]);
  // #471: `unit` arranca VACÍO, no en 'unidad'. Antes vivía hardcodeado acá y no
  // tenía input: Jorge armaba la factura, revisaba el preview y guardaba sin ver
  // nunca la palabra "unidad" — que aparecía por primera vez en el PDF del
  // cliente. Vacío = nadie la especificó y se guarda como NULL (el PDF lo
  // renderiza como "-"). Misma familia que #356 / #366 / #362.
  const EMPTY_ITEM = {
    description: '',
    item_type: 'service',
    quantity: 1,
    unit: '',
    unit_price: 0,
  };
  const [newItem, setNewItem] = useState(EMPTY_ITEM);

  const loadProjects = React.useCallback(() => {
    setProjectsLoading(true);
    setProjectsError(null);
    fetchProjects()
      .then((list) => setProjects(list))
      .catch((err) => {
        setProjects([]);
        setProjectsError(err);
      })
      .finally(() => setProjectsLoading(false));
  }, []);

  // --- Reset when modal opens ---
  useEffect(() => {
    if (show) {
      setSource('manual');
      setSelectedQuotation('');
      setAvailableQuotations([]);
      setSelectedClient(null);
      setFormData({
        invoice_date: new Date().toISOString().split('T')[0],
        payment_terms: 30,
        tax_rate: DEFAULT_TAX_RATE,
        notes: '',
      });
      setItems([]);

      // Use the pre-selected projectId if provided
      const initialProject = projectId || '';
      setSelectedProjectId(initialProject);

      // Load all projects for the selector
      loadProjects();

      // If a project is pre-selected, load its client
      if (initialProject) {
        fetchClient(null); // reset; will be loaded via project selection
      }
    }
  }, [show, projectId, loadProjects]);

  // When the selected project changes, auto-fill client and reset quotation state
  useEffect(() => {
    if (!selectedProjectId) {
      setSelectedClient(null);
      setAvailableQuotations([]);
      setSelectedQuotation('');
      return;
    }

    const proj = projects.find((p) => p.id === selectedProjectId);
    if (proj?.client_id) {
      fetchClient(proj.client_id).then(setSelectedClient);
    } else {
      setSelectedClient(null);
    }

    setAvailableQuotations([]);
    setSelectedQuotation('');
  }, [selectedProjectId, projects]);

  // When source or project changes, load quotations if in "quotation" mode
  useEffect(() => {
    if (source === 'quotation' && selectedProjectId) {
      setQuotationsLoading(true);
      fetchQuotationsForProject(selectedProjectId)
        .then((list) => {
          setAvailableQuotations(list);
          setSelectedQuotation('');
        })
        .catch(() => setAvailableQuotations([]))
        .finally(() => setQuotationsLoading(false));
    } else if (source === 'manual') {
      setAvailableQuotations([]);
      setSelectedQuotation('');
    }
  }, [source, selectedProjectId]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddItem = () => {
    // #471: la cantidad tiene que ser > 0 para agregar. Multiplica el precio y
    // define el monto cobrado; la columna es CHECK (quantity > 0), así que un 0
    // no se puede guardar y un blanco no se puede asumir como 1.
    if (newItem.description.trim() && newItem.unit_price > 0 && newItem.quantity > 0) {
      // Unidad en blanco viaja como null ("no se especificó"), nunca como ''
      // ni rellenada con 'unidad'.
      const unit = newItem.unit.trim() || null;
      setItems((prev) => [...prev, { ...newItem, unit }]);
      setNewItem(EMPTY_ITEM);
    }
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  const taxAmount = subtotal * (formData.tax_rate / 100);
  const total = subtotal + taxAmount;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (source === 'quotation' && selectedQuotation) {
      onSave({
        type: 'from_quotation',
        quotation_id: selectedQuotation,
        project_id: selectedProjectId || projectId,
        invoice_date: formData.invoice_date,
        payment_terms: parseInt(formData.payment_terms),
        notes: formData.notes,
      });
    } else {
      const dueDate = new Date(formData.invoice_date);
      dueDate.setDate(dueDate.getDate() + parseInt(formData.payment_terms));

      onSave({
        type: 'manual',
        project_id: selectedProjectId || projectId,
        invoice_data: {
          project_id: selectedProjectId || projectId,
          client_id: selectedClient?.id || null,
          invoice_number: `INV-${Date.now().toString(36).toUpperCase()}`,
          invoice_date: formData.invoice_date,
          due_date: dueDate.toISOString().split('T')[0],
          subtotal,
          tax_rate: parseFloat(formData.tax_rate),
          tax_amount: taxAmount,
          amount: total,
          payment_terms: parseInt(formData.payment_terms),
          status: 'draft',
          notes: formData.notes,
        },
        items: items.map((item, idx) => ({
          ...item,
          sort_order: idx,
        })),
      });
    }
  };

  const isProjectLocked = !!projectId; // project pre-selected from parent
  const canSubmit =
    !loading &&
    !!(selectedProjectId || projectId) &&
    (source === 'manual' ? items.length > 0 : !!selectedQuotation);

  return (
    // `centered` + body largo = el footer (donde vive "Crear factura") queda
    // bajo el fold en un telefono, porque el modal se centra sobre el alto de
    // la ventana en vez de scrollear. `scrollable` deja que scrollee SOLO el
    // body y clava el footer; `fullscreen="sm-down"` usa toda la pantalla en el
    // telefono, que es donde Jorge transcribe la factura de papel.
    <Modal show={show} onHide={onHide} centered size="lg" fullscreen="sm-down" scrollable>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Nueva Factura</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Source selection */}
          <Form.Group className="mb-3">
            <Form.Label>Origen</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="source-manual"
                label="Manual"
                name="source"
                checked={source === 'manual'}
                onChange={() => setSource('manual')}
              />
              <Form.Check
                type="radio"
                id="source-quotation"
                label="Desde Cotización"
                name="source"
                checked={source === 'quotation'}
                onChange={() => setSource('quotation')}
              />
            </div>
          </Form.Group>

          {/* Project selector (shown in both modes unless pre-locked) */}
          {!isProjectLocked && (
            <Form.Group className="mb-3">
              <Form.Label>
                Proyecto <span className="text-danger">*</span>
              </Form.Label>
              {projectsLoading ? (
                <div className="d-flex align-items-center gap-2">
                  <Spinner size="sm" animation="border" />
                  <span className="text-muted small">Cargando proyectos...</span>
                </div>
              ) : projectsError ? (
                <Alert variant="danger" className="mb-0 py-2">
                  <div className="small mb-2">
                    No se pudieron cargar los proyectos.{' '}
                    {projectsError.message || 'Error inesperado'}
                  </div>
                  <Button variant="outline-danger" size="sm" onClick={loadProjects}>
                    Reintentar
                  </Button>
                </Alert>
              ) : projects.length === 0 ? (
                <p className="text-muted small mb-0">
                  No hay proyectos disponibles para facturar.
                </p>
              ) : (
                <Form.Select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  required
                >
                  <option value="">Seleccionar proyecto...</option>
                  {projects.map((proj) => (
                    <option key={proj.id} value={proj.id}>
                      {proj.project_number} - {proj.title}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>
          )}

          {/* Client (auto-filled, read-only) */}
          {selectedClient && (
            <Form.Group className="mb-3">
              <Form.Label>Cliente</Form.Label>
              <Form.Control
                type="text"
                value={selectedClient.company_name}
                readOnly
                className="bg-light"
              />
            </Form.Group>
          )}

          {/* Quotation selector (only in "from quotation" mode) */}
          {source === 'quotation' && (
            <Form.Group className="mb-3">
              <Form.Label>
                Cotización Aprobada <span className="text-danger">*</span>
              </Form.Label>
              {!selectedProjectId ? (
                <p className="text-muted small mb-0">
                  Selecciona un proyecto primero.
                </p>
              ) : quotationsLoading ? (
                <div className="d-flex align-items-center gap-2">
                  <Spinner size="sm" animation="border" />
                  <span className="text-muted small">Cargando cotizaciones...</span>
                </div>
              ) : availableQuotations.length === 0 ? (
                <p className="text-muted small mb-0">
                  No hay cotizaciones aprobadas para este proyecto.
                </p>
              ) : (
                <Form.Select
                  value={selectedQuotation}
                  onChange={(e) => setSelectedQuotation(e.target.value)}
                  required
                >
                  <option value="">Seleccionar cotización...</option>
                  {availableQuotations.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.quotation_number} — ${q.total?.toFixed(2) || '0.00'} ({QUOTATION_STATUS_LABELS[q.status] || q.status})
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>
          )}

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Fecha de Factura</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) => handleChange('invoice_date', e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Plazo de Pago (días)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={formData.payment_terms}
                  onChange={(e) => handleChange('payment_terms', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Tasa de Impuesto (%)</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={formData.tax_rate}
                    onChange={(e) => handleChange('tax_rate', e.target.value)}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          {/* Manual items entry */}
          {source === 'manual' && (
            <>
              <h6 className="mt-4 mb-3">Conceptos</h6>
              {items.length > 0 && (
                <Table size="sm" responsive className="mb-3">
                  <thead>
                    <tr>
                      <th>Descripción</th>
                      <th>Tipo</th>
                      <th>Cant</th>
                      <th>Unidad</th>
                      <th>Precio</th>
                      <th className="text-end">Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.description}</td>
                        <td>{ITEM_TYPE_LABELS[item.item_type] || item.item_type}</td>
                        <td>{item.quantity}</td>
                        {/* #471: sin unidad se muestra "-", igual que en el PDF —
                            el hueco se ve, no se rellena. */}
                        <td className={item.unit ? '' : 'text-muted'}>{item.unit || '—'}</td>
                        <td>${item.unit_price.toFixed(2)}</td>
                        <td className="text-end">${(item.quantity * item.unit_price).toFixed(2)}</td>
                        <td>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={() => handleRemoveItem(idx)}
                          >
                            <i className="bi bi-x-lg"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5} className="text-end"><strong>Subtotal:</strong></td>
                      <td className="text-end"><strong>${subtotal.toFixed(2)}</strong></td>
                      <td></td>
                    </tr>
                    <tr>
                      <td colSpan={5} className="text-end">IVA ({formData.tax_rate}%):</td>
                      <td className="text-end">${taxAmount.toFixed(2)}</td>
                      <td></td>
                    </tr>
                    <tr>
                      <td colSpan={5} className="text-end"><strong>Total:</strong></td>
                      <td className="text-end"><strong>${total.toFixed(2)}</strong></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              )}

              <Row className="g-2 align-items-end">
                <Col xs={12} md={6}>
                  <Form.Label className="small text-muted mb-1">Descripción</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Descripción"
                    value={newItem.description}
                    onChange={(e) => setNewItem((p) => ({ ...p, description: e.target.value }))}
                    size="sm"
                  />
                </Col>
                {/* xs={12}: en 375px una media columna deja ~160px por campo.
                    La fila de dos-en-dos sirve a la fila compacta de escritorio
                    a costa del telefono, que es donde Jorge transcribe la
                    factura de papel. En md+ la fila compacta se mantiene. */}
                <Col xs={12} md={3}>
                  <Form.Label className="small text-muted mb-1">Tipo</Form.Label>
                  <Form.Select
                    aria-label="Tipo"
                    value={newItem.item_type}
                    onChange={(e) => setNewItem((p) => ({ ...p, item_type: e.target.value }))}
                    size="sm"
                  >
                    {Object.entries(ITEM_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Label className="small text-muted mb-1">Cantidad</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="Cant"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem((p) => ({ ...p, quantity: parseFloat(e.target.value) || 0 }))}
                    size="sm"
                  />
                </Col>
                {/* #471: la unidad ahora se teclea acá. Dejarla en blanco es una
                    respuesta válida ("no se especificó") y se guarda como NULL —
                    el sistema NO la rellena con 'unidad' por su cuenta. */}
                <Col xs={12} md={4}>
                  <Form.Label className="small text-muted mb-1">
                    Unidad <span className="text-muted fw-normal">(opcional)</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    aria-label="Unidad"
                    placeholder="horas, litros, m2…"
                    value={newItem.unit}
                    onChange={(e) => setNewItem((p) => ({ ...p, unit: e.target.value }))}
                    size="sm"
                  />
                </Col>
                <Col xs={12} md={4}>
                  <Form.Label className="small text-muted mb-1">Precio unitario</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Precio"
                    value={newItem.unit_price || ''}
                    onChange={(e) => setNewItem((p) => ({ ...p, unit_price: parseFloat(e.target.value) || 0 }))}
                    size="sm"
                  />
                </Col>
                <Col xs={12} md={4}>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddItem}
                    disabled={
                      !newItem.description.trim() ||
                      newItem.unit_price <= 0 ||
                      !(newItem.quantity > 0)
                    }
                    className="w-100"
                  >
                    <i className="bi bi-plus-lg me-1"></i>Agregar
                  </Button>
                </Col>
              </Row>
            </>
          )}

          <Form.Group className="mt-3">
            <Form.Label>Notas</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Notas adicionales..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-1" />
                Creando...
              </>
            ) : (
              'Crear Factura'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default InvoiceForm;
